import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import * as LucideIcons from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const hardcodedPosts = [
  {
    id: 'how-to-choose-construction-company',
    title: 'How to Choose the Right Construction Company for Your Home Project',
    excerpt: 'Discover the essential factors to consider when selecting a construction company, from credentials to communication, ensuring your project is in safe hands.',
    image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800',
    date: 'January 7, 2025',
    readTime: '8 min read',
    category: 'Guides',
    featured: true
  },
  {
    id: 'winter-construction-tips',
    title: 'Essential Winter Construction Tips: Protecting Your Project in Cold Weather',
    excerpt: 'Learn how to plan and execute construction projects during winter months. From frost protection to material storage, ensure your project stays on track.',
    image: 'https://images.unsplash.com/photo-1516557070061-c3d1653fa646?w=800',
    date: 'January 6, 2025',
    readTime: '7 min read',
    category: 'Seasonal'
  },
  {
    id: 'spring-renovation-guide',
    title: 'Spring Renovation Guide: The Perfect Time to Transform Your Home',
    excerpt: 'Spring is ideal for home improvements. Discover why this season offers the best conditions for painting, landscaping, and exterior work.',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800',
    date: 'January 5, 2025',
    readTime: '6 min read',
    category: 'Seasonal'
  },
  {
    id: 'summer-building-projects',
    title: 'Summer Building Projects: Making the Most of Long Days',
    excerpt: 'Take advantage of summer weather for major construction projects. Extensions, roofing, and outdoor spaces are best tackled in warmer months.',
    image: 'https://images.unsplash.com/photo-1513880989635-6eb491ce7f5b?w=800',
    date: 'January 4, 2025',
    readTime: '6 min read',
    category: 'Seasonal'
  },
  {
    id: 'kitchen-renovation-trends-2025',
    title: 'Top Kitchen Renovation Trends for 2025',
    excerpt: 'From smart appliances to sustainable materials, explore the latest kitchen design trends that are transforming homes across the UK.',
    image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800',
    date: 'January 3, 2025',
    readTime: '6 min read',
    category: 'Trends'
  },
  {
    id: 'bathroom-transformation-guide',
    title: 'The Complete Guide to Bathroom Transformations',
    excerpt: 'Planning a bathroom renovation? Learn about timelines, budgets, and design choices that will help you create your dream bathroom.',
    image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800',
    date: 'January 2, 2025',
    readTime: '7 min read',
    category: 'Guides'
  }
];

const Blog = () => {
  const [apiPosts, setApiPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/blog`).then(res => {
      setApiPosts(res.data);
    }).catch(() => {});
  }, []);

  // Merge API posts with hardcoded, API posts first, avoid duplicates by id
  const apiIds = new Set(apiPosts.map(p => p.id));
  const blogPosts = [...apiPosts, ...hardcodedPosts.filter(p => !apiIds.has(p.id))];

  const featuredPost = blogPosts.find(post => post.featured);
  const otherPosts = blogPosts.filter(post => !post.featured);

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Blog - Construction Tips & Guides"
        description="Expert advice, tips, and guides for your construction and renovation projects. Learn from T&M Construction's years of experience."
        canonical="/blog"
      />
      
      {/* Hero Section */}
      <section className="pt-56 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-orange-600 font-semibold text-sm uppercase tracking-wider">Our Blog</span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-2 mb-6">Construction Tips & Insights</h1>
            <p className="text-xl text-gray-600">
              Expert advice and guides to help you make informed decisions about your property
            </p>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4">
            <Link to={`/blog/${featuredPost.id}`}>
              <div className="max-w-6xl mx-auto">
                <div className="relative rounded-2xl overflow-hidden group cursor-pointer shadow-xl">
                  <img 
                    src={featuredPost.image} 
                    alt={featuredPost.title}
                    className="w-full h-96 md:h-[500px] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  <div className="absolute top-6 left-6">
                    <span className="bg-orange-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Featured
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
                    <div className="flex items-center space-x-4 text-gray-300 text-sm mb-4">
                      <span>{featuredPost.date}</span>
                      <span>•</span>
                      <span>{featuredPost.readTime}</span>
                      <span>•</span>
                      <span className="text-orange-400">{featuredPost.category}</span>
                    </div>
                    <h2 className="text-2xl md:text-4xl font-bold text-white mb-4 group-hover:text-orange-400 transition-colors">
                      {featuredPost.title}
                    </h2>
                    <p className="text-gray-200 text-lg max-w-2xl mb-6">{featuredPost.excerpt}</p>
                    <div className="flex items-center text-orange-400 font-semibold group-hover:translate-x-2 transition-transform">
                      Read Article
                      <LucideIcons.ArrowRight className="w-5 h-5 ml-2" />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Other Posts */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">More Articles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {otherPosts.map((post) => (
              <Link key={post.id} to={`/blog/${post.id}`}>
                <Card className="overflow-hidden group cursor-pointer hover:shadow-xl transition-all duration-300 h-full">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <span className="absolute top-4 left-4 bg-white/90 text-gray-700 px-3 py-1 rounded-full text-xs font-medium">
                      {post.category}
                    </span>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-3 text-gray-500 text-sm mb-3">
                      <span>{post.date}</span>
                      <span>•</span>
                      <span>{post.readTime}</span>
                    </div>
                    <h3 className="font-bold text-lg text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">{post.excerpt}</p>
                    <div className="flex items-center text-orange-600 font-medium text-sm group-hover:translate-x-2 transition-transform">
                      Read More
                      <LucideIcons.ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Start Your Project?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Get in touch with our team for a free, no-obligation quote
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white px-8">
              Get Your Free Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Blog;
